import { create } from 'zustand';
import type {
  AgentLive,
  AuthUser,
  ChatMessage,
  ChatView,
  DigitalOps,
  EmailMessage,
  EmailView,
  Disposition,
  LiveCall,
  MonitorView,
  NovaEvent,
  Person,
  QueueRow,
  QueueStat,
  Snapshot,
} from './types';

export type View = 'home' | 'cockpit' | 'ops' | 'history' | 'recordings' | 'quality' | 'reports' | 'admin';
export type LinkStatus = 'connecting' | 'live' | 'reconnecting';
export type PhoneStatus = 'off' | 'connecting' | 'registered' | 'failed';

export interface WrapUp {
  interactionId: string;
  call: LiveCall;
  customer: string;
  talkSeconds: number;
  required: boolean;
}

export interface Toast {
  id: number;
  tone: 'info' | 'error' | 'success';
  text: string;
}

export interface OrgInfo {
  id: string;
  slug: string;
  name: string;
  timezone: string;
  branding: { crmUrl?: string; logo?: string } | null;
}

interface NovaState {
  user: AuthUser | null;
  org: OrgInfo | null;
  email: string | null;
  view: View;
  link: LinkStatus;
  voice: boolean;
  phone: PhoneStatus;
  phoneError: string | null;
  serverOffset: number;
  lastSeq: number;

  me: AgentLive | null;
  myStats: { handled: number; avg_talk: number };
  agents: Record<string, AgentLive>;
  people: Record<string, Person>;
  calls: Record<string, LiveCall>;
  monitors: MonitorView[];
  queues: QueueStat[];
  queueList: QueueRow[];
  dispositions: Disposition[];
  breakReasons: Disposition[];
  feed: NovaEvent[];
  wrap: WrapUp | null;
  /** The SIP session is ringing on this device */
  ringing: { interactionId: string | null; from: string; answering?: boolean } | null;
  /** This device is calling one of our own numbers to test it (the caller's side) */
  testCall: { number: string; since: number | null; muted: boolean } | null;
  /** My chats being worked on (offered, live, wrap-up), by interaction */
  chats: Record<string, ChatView>;
  /** Messages of chats I have opened, by interaction */
  chatMessages: Record<string, ChatMessage[]>;
  /** Until when the visitor is shown typing, by interaction */
  chatTyping: Record<string, number>;
  /** My emails being worked on (open, wrap-up), by interaction */
  emails: Record<string, EmailView>;
  /** Conversations of emails I have opened, by interaction */
  emailMessages: Record<string, EmailMessage[]>;
  /** Add-ons switched on for the organization: qa */
  features: Record<string, boolean>;
  /** Supervisors: every chat and email waiting or being worked on, kept live from events */
  digital: DigitalOps | null;
  /** This device's microphone is off (calls the API does not mute: test calls, a colleague's call to me) */
  phoneMuted: boolean;
  toasts: Toast[];
  paletteOpen: boolean;
  /** Interaction open in the work area */
  selectedId: string | null;

  set: (patch: Partial<NovaState>) => void;
  applySnapshot: (s: Snapshot) => void;
  applyEvent: (e: NovaEvent) => 'ok' | 'gap';
  toast: (text: string, tone?: Toast['tone']) => void;
  dismiss: (id: number) => void;
  reset: () => void;
}

const empty = {
  user: null,
  org: null,
  email: null,
  view: 'cockpit' as View,
  link: 'connecting' as LinkStatus,
  voice: false,
  phone: 'off' as PhoneStatus,
  phoneError: null,
  serverOffset: 0,
  lastSeq: 0,
  me: null,
  myStats: { handled: 0, avg_talk: 0 },
  agents: {},
  people: {},
  calls: {},
  monitors: [],
  queues: [],
  queueList: [],
  dispositions: [],
  breakReasons: [],
  feed: [],
  wrap: null,
  ringing: null,
  testCall: null,
  phoneMuted: false,
  chats: {},
  chatMessages: {},
  chatTyping: {},
  emails: {},
  emailMessages: {},
  digital: null,
  features: {},
  toasts: [],
  paletteOpen: false,
  selectedId: null,
};

let toastId = 0;

export const useNova = create<NovaState>((set, get) => ({
  ...empty,

  set: (patch) => set(patch),

  applySnapshot: (s) =>
    set({
      lastSeq: s.seq,
      voice: s.voice,
      serverOffset: s.serverTime - Date.now(),
      me: s.me,
      myStats: s.myStats,
      agents: Object.fromEntries(s.agents.map((a) => [a.userId, a])),
      calls: Object.fromEntries(s.calls.map((c) => [c.interactionId, c])),
      monitors: s.monitors ?? [],
      queues: s.queues,
      features: s.features ?? {},
    }),

  applyEvent: (e) => {
    const st = get();
    // A gap means events for others were skipped (or missed): fetch the snapshot again. Chat, email
    // and quality events still apply, as the snapshot has none of what they carry and applying one
    // twice changes nothing (a quality event only raises a notice).
    const gap = Boolean(st.lastSeq && e.seq > st.lastSeq + 1);
    if (gap && !e.type.startsWith('chat.') && !e.type.startsWith('email.') && !e.type.startsWith('qa.')) return 'gap';
    if (!gap && e.seq <= st.lastSeq) return 'ok';
    const patch: Partial<NovaState> = { lastSeq: gap ? st.lastSeq : e.seq, feed: [e, ...st.feed].slice(0, 80) };
    const me = st.user?.sub;

    if (e.type === 'agent.state_changed') {
      const a = e.data as unknown as AgentLive;
      patch.agents = { ...st.agents, [a.userId]: a };
      if (a.userId === me) patch.me = a;
    } else if (e.type === 'queue.stats') {
      const live = new Map(
        (e.data.queues as { queueId: string; waiting: number; oldestEnqueuedAt: number | null }[]).map((q) => [q.queueId, q]),
      );
      patch.queues = st.queues.map((q) => ({
        ...q,
        waiting: live.get(q.queue_id)?.waiting ?? 0,
        oldestEnqueuedAt: live.get(q.queue_id)?.oldestEnqueuedAt ?? null,
      }));
    } else if (e.type.startsWith('call.')) {
      const c = e.data as unknown as LiveCall & { outcome?: string; talkSeconds?: number; requireDisposition?: boolean };
      const calls = { ...st.calls };
      if (e.type === 'call.ended' || e.type === 'call.abandoned') {
        delete calls[c.interactionId];
        const q = st.queues.find((x) => x.queue_id === c.queueId);
        if (q && c.direction === 'inbound') {
          patch.queues = st.queues.map((x) =>
            x.queue_id === q.queue_id
              ? { ...x, offered: x.offered + 1, abandoned: x.abandoned + (e.type === 'call.abandoned' ? 1 : 0), answered: x.answered + (c.answeredAt ? 1 : 0) }
              : x,
          );
        }
        const note = (e.data as { note?: string | null }).note;
        const why = note ? NOT_REACHED[note] : undefined;
        if (c.direction === 'internal' && c.agentId === me && c.outcome === 'failed' && why) {
          setTimeout(() => get().toast(`${c.customerName ?? 'Your colleague'} ${why}`, 'info'), 0);
        }
        // Calls between colleagues have no wrap-up and do not count as handled
        if (c.agentId === me && e.type === 'call.ended' && c.outcome === 'completed' && c.direction !== 'internal') {
          patch.wrap = {
            interactionId: c.interactionId,
            call: c,
            customer: c.customerName || c.customerNumber,
            talkSeconds: c.talkSeconds ?? 0,
            required: Boolean(c.requireDisposition),
          };
          const n = st.myStats.handled;
          patch.myStats = { handled: n + 1, avg_talk: Math.round((st.myStats.avg_talk * n + (c.talkSeconds ?? 0)) / (n + 1)) };
        }
      } else if (e.type === 'call.transferred' && (e.data as { fromAgentId?: string }).fromAgentId === me) {
        calls[c.interactionId] = c;
        patch.wrap = null;
      } else {
        calls[c.interactionId] = c;
      }
      patch.calls = calls;
    } else if (e.type.startsWith('chat.')) {
      // My chats only; supervisors hear about every chat but work their own here
      const v = e.data as unknown as ChatView & { message?: ChatMessage; by?: string };
      const id = v.interactionId;
      const known = st.chats[id];
      if (e.type === 'chat.typing') {
        if (v.by === 'visitor' && known) patch.chatTyping = { ...st.chatTyping, [id]: Date.now() + 5000 };
      } else if (e.type === 'chat.message') {
        if (v.message && st.chatMessages[id] && !st.chatMessages[id].some((m) => m.id === v.message!.id)) {
          patch.chatMessages = { ...st.chatMessages, [id]: [...st.chatMessages[id], v.message] };
        }
        if (v.message?.sender === 'customer') patch.chatTyping = { ...st.chatTyping, [id]: 0 };
        if (known && v.status) patch.chats = { ...st.chats, [id]: { ...known, ...stripMessage(v) } };
      } else if (v.agentId === me && v.status && ['ringing', 'connected', 'wrap_up'].includes(v.status)) {
        patch.chats = { ...st.chats, [id]: stripMessage(v) };
      } else if (known) {
        // Taken back (missed, declined), or over without a conversation
        const { [id]: _gone, ...rest } = st.chats;
        patch.chats = rest;
      }
    } else if (e.type.startsWith('email.')) {
      // My emails only; supervisors hear about every email but work their own here
      const v = e.data as unknown as EmailView & { message?: EmailMessage };
      const { message, ...view } = v;
      const id = v.interactionId;
      if (message && st.emailMessages[id] && !st.emailMessages[id].some((m) => m.id === message.id)) {
        patch.emailMessages = { ...st.emailMessages, [id]: [...st.emailMessages[id], message] };
      }
      if (view.agentId === me && ['connected', 'wrap_up'].includes(view.status)) {
        patch.emails = { ...st.emails, [id]: view as EmailView };
      } else if (st.emails[id]) {
        // Given to someone else
        const { [id]: _gone, ...rest } = st.emails;
        patch.emails = rest;
      }
    }
    // Supervisors' live view of chats and email, whoever is working them
    if (st.digital && (e.type.startsWith('chat.') || e.type.startsWith('email.') || e.type === 'interaction.dispositioned')) {
      patch.digital = trackDigital(st.digital, e);
    }
    if (e.type === 'queue.closure') {
      const d = e.data as { queueId: string; closed: boolean };
      patch.queues = st.queues.map((q) => (q.queue_id === d.queueId ? { ...q, emergency_closed: d.closed } : q));
    } else if (e.type === 'monitor.changed') {
      patch.monitors = (e.data as { monitors: MonitorView[] }).monitors;
    } else if (e.type === 'interaction.dispositioned') {
      if (st.wrap?.interactionId === (e.data.interactionId as string)) patch.wrap = null;
      if (st.chats[e.data.interactionId as string]) {
        const { [e.data.interactionId as string]: _done, ...rest } = st.chats;
        patch.chats = rest;
      }
      if (st.emails[e.data.interactionId as string]) {
        const { [e.data.interactionId as string]: _done, ...rest } = st.emails;
        patch.emails = rest;
      }
    }
    set(patch);
    return gap ? 'gap' : 'ok';
  },

  toast: (text, tone = 'info') => {
    const id = ++toastId;
    set({ toasts: [...get().toasts, { id, text, tone }].slice(-4) });
    setTimeout(() => get().dismiss(id), tone === 'error' ? 7000 : 4000);
  },
  dismiss: (id) => set({ toasts: get().toasts.filter((t) => t.id !== id) }),
  reset: () => set({ ...empty }),
}));

/** Why a colleague's phone did not pick up, by the call's end note. Hanging up yourself says nothing. */
const NOT_REACHED: Record<string, string> = {
  Busy: 'is busy',
  Declined: 'declined the call',
  'No answer': "didn't answer",
  'Their softphone is not connected': "isn't reachable: their softphone is not connected",
};

/** The live call this agent is on or being offered, if any. */
export function myCall(s: Pick<NovaState, 'calls' | 'user'>): LiveCall | null {
  // Mine: the calls I handle, a call I was brought into by a consult or conference, and a colleague calling me
  const me = s.user?.sub;
  const mine = Object.values(s.calls).filter((c) => (c.agentId === me || c.third?.agentId === me || c.peerId === me) && c.status !== 'ended');
  return mine.sort((a, b) => b.startedAt - a.startedAt)[0] ?? null;
}

/** On a call between colleagues, the other one: who they are and how to show them. */
export function otherParty(call: LiveCall, s: Pick<NovaState, 'people' | 'user'>): { id: string | null; name: string; extension: string; team: string | null } {
  const calledMe = call.peerId === s.user?.sub;
  const id = calledMe ? call.agentId : call.peerId;
  const person = id ? s.people[id] : undefined;
  return {
    id,
    name: (calledMe ? call.agentName : call.customerName) ?? person?.name ?? 'A colleague',
    extension: person?.extension ?? (calledMe ? '' : call.customerNumber),
    team: person?.team ?? null,
  };
}

/** The test call I am making, as the number's IVR, queue and agents see it */
export function myTestCall(s: Pick<NovaState, 'calls' | 'user'>): LiveCall | null {
  return Object.values(s.calls).find((c) => c.testerId === s.user?.sub && c.status !== 'ended') ?? null;
}

export const STATE_LABEL: Record<AgentLive['state'], string> = {
  available: 'Available',
  reserved: 'Ringing',
  on_call: 'On call',
  wrap_up: 'Wrap-up',
  break: 'Break',
  away: 'Away',
  offline: 'Offline',
};

export const STATE_COLOR: Record<AgentLive['state'], string> = {
  available: 'var(--color-available)',
  reserved: 'var(--color-reserved)',
  on_call: 'var(--color-oncall)',
  wrap_up: 'var(--color-wrapup)',
  break: 'var(--color-break)',
  away: 'var(--color-away)',
  offline: 'var(--color-offline)',
};

export const REASON_LABEL: Record<string, string> = {
  signed_in: 'Just signed in',
  missed_call: 'Missed a call',
  unreachable: 'Softphone unreachable',
  no_softphone: 'No softphone',
  disconnected: 'Disconnected',
  deactivated: 'Deactivated',
  supervisor: 'Set by supervisor',
  monitoring: 'Listening to a call',
  test_call: 'Making a test call',
  signed_out: 'Signed out by supervisor',
  suspended: 'Organization suspended',
  lunch: 'Lunch',
  short: 'Short break',
  training: 'Training',
  meeting: 'Meeting',
};

/** Folds a chat or email event into the supervisors' live view. */
function trackDigital(d: DigitalOps, e: NovaEvent): DigitalOps {
  const id = e.data.interactionId as string;
  if (e.type === 'interaction.dispositioned') {
    if (!d.chats[id] && !d.emails[id]) return d;
    const { [id]: _c, ...chats } = d.chats;
    const { [id]: _e, ...emails } = d.emails;
    return { ...d, chats, emails };
  }
  // Events without the conversation's view (typing, system lines) change nothing here
  const status = (e.data as { status?: string }).status;
  if (!status) return d;
  const { message: _m, by: _b, ...view } = e.data as Record<string, unknown>;
  if (e.type.startsWith('chat.')) {
    if (['queued', 'ringing', 'connected', 'wrap_up'].includes(status)) return { ...d, chats: { ...d.chats, [id]: view as unknown as ChatView } };
    const { [id]: _gone, ...chats } = d.chats;
    return { ...d, chats };
  }
  if (['queued', 'connected', 'wrap_up'].includes(status)) return { ...d, emails: { ...d.emails, [id]: view as unknown as EmailView } };
  const { [id]: _gone, ...emails } = d.emails;
  return { ...d, emails };
}

function stripMessage(v: ChatView & { message?: ChatMessage; by?: string }): ChatView {
  const { message: _m, by: _b, ...view } = v;
  return view as ChatView;
}
